import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  model,
  Output,
  TemplateRef,
  ViewChildren,
  QueryList,
  AfterViewInit,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { DxFormModule, DxPopupModule, DxValidationGroupModule, DxValidationGroupComponent } from 'devextreme-angular';
import { ClickEvent } from 'devextreme/ui/button';
import { IconName } from 'src/app/helpers/icon-exports';
import { GpdButtonComponent } from '../gpd-button/gpd-button.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { GpdStepperComponent, StepperItem, StepperNavigationEvent } from '../gpd-stepper/gpd-stepper.component';
import { DxMultiViewComponent, DxiMultiViewItemComponent } from 'devextreme-angular/ui/multi-view';

export interface Step {
  id: string;
  title: string;
  icon: IconName;
  description?: string;
  template: TemplateRef<any>;
  isValid?: boolean;
  disabled?: boolean;
}

export interface StepNavigatedEvent {
  toStep?: Step;
  fromStep: Step;
  clickEvent: ClickEvent;
}

@Component({
    selector: 'gpd-multi-step-popup',
    imports: [
        CommonModule,
        DxFormModule,
        DxPopupModule,
        GpdButtonComponent,
        SvgIconComponent,
        DxValidationGroupModule,
        GpdStepperComponent,
        DxMultiViewComponent,
        DxiMultiViewItemComponent,
    ],
    templateUrl: './multi-step-popup.component.html',
    styleUrl: './multi-step-popup.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiStepPopupComponent implements AfterViewInit, OnDestroy {
  visible = model(false);
  width = model<string | number>('auto');
  height = model<string | number>('auto');

  title = input.required<string>();
  titleIcon = input<IconName>();
  steps = input.required<Step[]>();

  maxHeight = input<string | number>();
  allowOutsideClick = input(false);
  /**
   * Off by default: step content is usually grids/forms that must exist before the
   * popup is measured. Set true to only render the content on first show.
   */
  deferRendering = input(false);

  finishText = input<string>($localize`:@@finish:Finish`);

  stepperItems = computed(() => {
    return this.steps()?.reduce((acc, step, index) => {
      let disabled = false;

      // Disable step if previous step is invalid or disabled
      if (index > 0) {
        const previousStepperItem = acc[index - 1];
        if (previousStepperItem.disabled || !this.steps()[index - 1].isValid) {
          disabled = true;
        }
      }

      acc.push({ ...step, disabled: disabled });

      return acc;
    }, [] as StepperItem[]);
  });

  @Output() onNextClicked = new EventEmitter<StepNavigatedEvent>();
  @Output() onPreviousClicked = new EventEmitter<StepNavigatedEvent>();
  /** Emitted when the popup is dismissed via the close (X) button. */
  @Output() onCancelClicked = new EventEmitter<void>();
  @Output() onFinishClicked = new EventEmitter<StepNavigatedEvent>();
  @Output() onShowing = new EventEmitter<void>();
  @Output() onHiding = new EventEmitter<void>();

  // State
  currentStepIndex = signal(0);
  currentStepIsValid = signal(true);

  @ViewChild(DxMultiViewComponent)
  multiView!: DxMultiViewComponent;

  @ViewChildren(DxValidationGroupComponent)
  validationGroups!: QueryList<DxValidationGroupComponent>;

  // Computed properties for button states
  isNextButtonDisabled = computed(() => !this.currentStepIsValid());
  isFinishButtonDisabled = computed(() => !this.currentStepIsValid());

  // Computed property for current step
  currentStep = computed(() => this.steps()[this.currentStepIndex()] || null);

  // Validation monitoring
  private validationInterval?: number;
  private lastValidationState?: boolean;

  ngAfterViewInit() {
    // Validate initial step. Monitoring only starts once the popup is actually shown —
    // the component is usually instantiated with its host page and would otherwise poll
    // for the whole time that page is open.
    this.updateCurrentStepValidationState();
  }

  ngOnDestroy() {
    // Clean up validation monitoring
    this.cleanupValidationMonitoring();
  }

  onShowingHandler() {
    this.setupValidationMonitoring();
    this.updateCurrentStepValidationState();
    this.onShowing.emit();
  }

  onHidingHandler() {
    this.cleanupValidationMonitoring();
    this.onHiding.emit();
  }

  /** The X in the header replaces the multi-step form's bottom-left Cancel button. */
  onCloseBtnClicked(e: ClickEvent) {
    this.visible.set(false);
    this.onCancelClicked.emit();
  }

  onNextBtnClicked(e: ClickEvent) {
    const isValid = this.validateCurrentStep();

    if (!isValid) {
      return; // Don't proceed if validation fails
    }

    const nextIndex = this.currentStepIndex() + 1;
    if (nextIndex < this.steps().length) {
      this.currentStepIndex.set(nextIndex);
      // Reset validation monitoring for new step
      this.cleanupValidationMonitoring();
      this.setupValidationMonitoring();
      this.updateCurrentStepValidationState();

      this.onNextClicked.emit({
        toStep: this.steps()[nextIndex],
        fromStep: this.steps()[nextIndex - 1],
        clickEvent: e,
      });
    }
  }

  onPreviousBtnClicked(e: ClickEvent) {
    const prevIndex = this.currentStepIndex() - 1;
    if (prevIndex >= 0) {
      this.currentStepIndex.set(prevIndex);
      // Reset validation monitoring for new step
      this.cleanupValidationMonitoring();
      this.setupValidationMonitoring();
      this.updateCurrentStepValidationState();

      this.onPreviousClicked.emit({
        toStep: this.steps()[prevIndex],
        fromStep: this.steps()[prevIndex + 1],
        clickEvent: e,
      });
    }
  }

  onFinishBtnClicked(e: ClickEvent) {
    const isValid = this.validateCurrentStep();

    if (!isValid) {
      return; // Don't proceed if validation fails
    }

    this.onFinishClicked.emit({
      toStep: undefined,
      fromStep: this.currentStep(),
      clickEvent: e,
    });
  }

  /**
   * Validates the current step using DevExtreme's ValidationGroup API
   */
  validateCurrentStep(): boolean {
    const currentStep = this.currentStep();
    if (!currentStep) {
      return true;
    }

    const validationGroup = this.findValidationGroup(currentStep.id);
    if (!validationGroup) {
      return true; // No validation group means no validation required
    }

    try {
      const result = validationGroup.instance.validate();
      return result.isValid;
    } catch (error) {
      // Log error but allow progression to avoid blocking user
      console.warn(`Validation error for step ${currentStep.id}:`, error);
      return true;
    }
  }

  /**
   * Finds the validation group for a specific step ID
   */
  private findValidationGroup(stepId: string): DxValidationGroupComponent | undefined {
    return this.validationGroups?.find(group => group.instance?.element()?.id === `validation-group-${stepId}`);
  }

  onStepperSelectionChanged(event: StepperNavigationEvent) {
    // Only allow forward navigation if current step is valid
    if (event.currentIndex > this.currentStepIndex() && !this.validateCurrentStep()) {
      return; // Block navigation if validation fails
    }

    this.currentStepIndex.set(event.currentIndex);
    // Reset validation monitoring for new step
    this.cleanupValidationMonitoring();
    this.setupValidationMonitoring();
    this.updateCurrentStepValidationState();
  }

  /**
   * Updates the validation state for the current step
   */
  updateCurrentStepValidationState(): void {
    const isValid = this.validateCurrentStep();
    this.currentStepIsValid.set(isValid);
  }

  /**
   * Public method to trigger validation update from external components
   * This is called when data changes that may affect validation
   */
  public triggerValidationUpdate(): void {
    this.updateCurrentStepValidationState();
  }

  /**
   * Programmatic equivalent of clicking Next/Finish (whichever applies to the current
   * step) — for inline controls where completing a field via keyboard (e.g. pressing
   * Enter to commit a value) can be the last thing the step needed, so it can advance/
   * finish immediately instead of leaving the user to follow up with a separate click.
   * Reuses onNextBtnClicked/onFinishBtnClicked as-is, so it already no-ops when the step
   * isn't actually valid yet.
   */
  public triggerAdvanceIfValid(): void {
    if (this.currentStepIndex() === this.steps().length - 1) {
      this.onFinishBtnClicked(undefined as unknown as ClickEvent);
    } else {
      this.onNextBtnClicked(undefined as unknown as ClickEvent);
    }
  }

  /**
   * Public method to reset validation for dynamic form changes
   * This is useful when new form inputs are dynamically added
   */
  public resetValidation(): void {
    // DevExtreme automatically handles validation group updates
    // Just trigger a validation state update
    this.updateCurrentStepValidationState();
  }

  /**
   * Resets the popup back to the first step.
   */
  public reset(): void {
    this.currentStepIndex.set(0);
    this.cleanupValidationMonitoring();
    this.setupValidationMonitoring();
    this.updateCurrentStepValidationState();
  }

  /**
   * Sets up validation monitoring to automatically update button states
   */
  private setupValidationMonitoring(): void {
    // Never poll a hidden popup — step navigation and reset() also call this.
    if (!this.visible()) {
      return;
    }

    this.cleanupValidationMonitoring();

    // Check validation state every 200ms to catch changes
    this.validationInterval = window.setInterval(() => {
      const currentValidationState = this.validateCurrentStep();

      // Only update if validation state changed to avoid unnecessary updates
      if (this.lastValidationState !== currentValidationState) {
        this.lastValidationState = currentValidationState;
        this.currentStepIsValid.set(currentValidationState);
      }
    }, 200);
  }

  /**
   * Cleans up validation monitoring
   */
  private cleanupValidationMonitoring(): void {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
      this.validationInterval = undefined;
    }
    this.lastValidationState = undefined;
  }
}
